import type { ProfileMeta } from '@apm/contracts';
import { useEffect, useMemo, useRef, useState } from 'react';
import { navigate, routeHref } from '../router';
import { useServiceFilter } from '../services/service-filter';
import { useLiveSelector } from '../store/useStream';
import { TIP } from '../terms';
import {
  formatBytes,
  formatCount,
  formatDuration,
  formatFull,
  formatMs,
  formatStamp,
} from '../time';
import {
  Button,
  Cell,
  type Column,
  EmptyState,
  ErrorText,
  Field,
  Page,
  type Range,
  RangePicker,
  Row,
  rangeMs,
  Section,
  Select,
  Table,
  Tooltip,
} from '../ui';
import { useProfileList } from './useProfiles';

/**
 * 화면 — CPU 기록 목록 (8회차 결정 4).
 *
 * 앱이 눈에 띄게 멈춘 순간에만 뜬 것들이라 건수가 많지 않다. 페이지 나눔 없이 최신 50건을 보여 준다.
 * 행을 누르면 그때 CPU를 쥐고 있던 함수 표로 간다.
 *
 * 19회차에 서비스·프로세스로 거르는 조건이 붙었고(후보 #2, 서버는 처음부터 받고 있었다)
 * "이벤트루프 p99가 임계값을 연속으로 넘은 순간" 같은 문구를 사람 말로 풀었다(C-19).
 */
const COLUMNS: readonly Column[] = [
  { label: '기록 시각', width: 'w-44' },
  { label: '서비스', width: 'w-40' },
  { label: '인스턴스', width: 'w-56', title: TIP.instance },
  { label: '그때 처리 지연', width: 'w-52' },
  { label: '길이', width: 'w-28' },
  { label: '표본', width: 'w-28' },
  { label: '크기', width: 'w-28' },
];

export function ProfilesPage({
  range,
  onRangeChange,
}: {
  /** 진단 하위 탭이 함께 쓰는 기간 (22-B회차). 목록 API에는 기간이 없어 화면에서 거른다 */
  range: Range;
  onRangeChange: (next: Range) => void;
}) {
  // 인스턴스 목록만 본다 (D-C1)
  const liveInstances = useLiveSelector((live) => live.instances);
  const service = useServiceFilter();
  const [instance, setInstance] = useState('');
  const previousService = useRef(service);
  useEffect(() => {
    if (previousService.current === service) return;
    previousService.current = service;
    setInstance('');
  }, [service]);
  const {
    loading,
    rows: allRows,
    error,
    reload,
  } = useProfileList({
    service: service === '' ? undefined : service,
    instance: instance === '' ? undefined : instance,
  });

  // 기간은 진단 메뉴의 것이다. 기록은 드물어 기간 밖의 것을 숨기되 몇 건인지는 말한다
  const bounds = useMemo(() => rangeMs(range), [range]);
  const rows = useMemo(
    () =>
      allRows.filter(
        (row) =>
          (bounds.from === undefined || row.startedAt >= bounds.from) &&
          (bounds.to === undefined || row.startedAt <= bounds.to),
      ),
    [allRows, bounds],
  );
  const hidden = allRows.length - rows.length;

  const instances = useMemo(() => {
    const names = new Set<string>();
    for (const snapshot of liveInstances) {
      if (service === '' || snapshot.service === service) names.add(snapshot.instance);
    }
    for (const row of allRows) {
      if (service === '' || row.service === service) names.add(row.instance);
    }
    return [...names].sort();
  }, [liveInstances, allRows, service]);

  return (
    <Page>
      <Section
        boxed
        title="CPU 기록"
        note={[
          '처리 지연이 기준값을 연속으로 넘은 순간에만 자동으로 CPU 사용 내역을 기록한다.',
          '행을 누르면 상위 함수 표를 본다. 평소에는 기록하지 않는다',
        ]}
        badge={
          rows.length > 0
            ? `${formatCount(rows.length)}건${hidden > 0 ? ` · 기간 밖 ${formatCount(hidden)}건 숨김` : ''}`
            : undefined
        }
        actions={
          <Button size="sm" onClick={reload} disabled={loading}>
            {loading ? '읽는 중…' : '새로고침'}
          </Button>
        }
      >
        <div className="mb-4">
          <RangePicker idPrefix="profiles" value={range} onChange={onRangeChange} />
        </div>

        {instances.length > 1 ? (
          <div className="mb-4 max-w-xs">
            <Field label="인스턴스" htmlFor="profiles-instance" hint={TIP.instance}>
              <Select
                id="profiles-instance"
                value={instance}
                onChange={setInstance}
                options={[
                  { value: '', label: '전체' },
                  ...instances.map((name) => ({ value: name, label: name })),
                ]}
              />
            </Field>
          </div>
        ) : null}

        {error !== undefined ? <ErrorText>{error}</ErrorText> : null}
        {!loading && error === undefined && rows.length === 0 ? (
          <EmptyState
            title={hidden > 0 ? '이 기간에 CPU 기록이 없다' : 'CPU 기록이 없다'}
            hint={
              hidden > 0
                ? `기간 밖에 ${formatCount(hidden)}건이 있다. 기간을 넓혀 볼 것`
                : '처리 지연이 기준값을 넘은 일이 없었다'
            }
          />
        ) : null}

        {rows.length > 0 ? <ProfileTable rows={rows} /> : null}
      </Section>
    </Page>
  );
}

function ProfileTable({ rows }: { rows: readonly ProfileMeta[] }) {
  return (
    <Table columns={COLUMNS} caption="CPU 기록" minWidth="min-w-[960px]" maxHeight="max-h-[640px]">
      {rows.map((meta) => (
        // 첫 칸의 링크가 키보드 경로다. 행 전체의 누르기는 마우스 편의로 남긴다 (13회차)
        <Row key={meta.id} onClick={() => navigate({ name: 'profile', id: meta.id })}>
          <Cell nowrap className="tabular">
            <a
              href={routeHref({ name: 'profile', id: meta.id })}
              title={formatFull(meta.startedAt)}
              className="-mx-1 block rounded px-1 text-inherit no-underline"
            >
              {formatStamp(meta.startedAt)}
            </a>
          </Cell>
          <Cell>{meta.service}</Cell>
          <Cell mono tone="muted" title={meta.instance} className="max-w-[220px] truncate">
            {meta.instance}
          </Cell>
          <Cell nowrap tone="error" className="tabular">
            <Tooltip text={TIP.loopDelay}>{formatMs(meta.trigger.loopP99Ms)}</Tooltip>
            <span className="ml-1.5 text-muted">
              (기준 {meta.trigger.thresholdMs}ms × {meta.trigger.consecutive}회 연속)
            </span>
          </Cell>
          <Cell nowrap className="tabular">
            {formatDuration(meta.durationMs)}
          </Cell>
          <Cell nowrap tone="muted" className="tabular">
            {formatCount(meta.sampleCount)}
          </Cell>
          <Cell nowrap tone="muted" className="tabular">
            {formatBytes(meta.sizeBytes)}
          </Cell>
        </Row>
      ))}
    </Table>
  );
}
